import { bool, func, oneOfType } from 'prop-types';
import React from 'react';
import { Col, FormGroup, Input, Label, Row } from 'reactstrap';

import DiscardableForm from '../../generic/DiscardableForm';
import MultiSelect from '../../generic/MultiSelect';
import SingleSelect from '../../generic/SingleSelect';
import TimestampInfo from '../../generic/TimestampInfo';
import { getTooltipLabelWithText } from '../../generic/Tooltip';
import { newUserShape, userShape } from '../../prop-types';
import convertToOption from '../../utils/convert-to-option';
import storedUserInfo from '../../utils/session-data/user-info';
import { uiVariantOptions } from '../constants';
import HELP_TEXTS from '../help-texts';
import { rolesThatCanBeSetBy } from '../utils';
import UserSalesUnit from './UserSalesUnit';

function UserForm({ model, onChange, onSubmit, newModel, onDiscard }) {
  const {
    number,
    firstName,
    lastName,
    email,
    mobile,
    team,
    roles,
    uiVariant,
    created,
    sapId,
    lastModified,
    createdBy,
    lastModifiedBy,
  } = model;

  const TooltipLabel = getTooltipLabelWithText(HELP_TEXTS);

  const inputProps = { onChange, readOnly: !onSubmit };
  const currentUser = storedUserInfo.get();
  const roleOptions = rolesThatCanBeSetBy(currentUser).map(convertToOption);

  return (
    <DiscardableForm onSubmit={onSubmit} onDiscard={onDiscard}>
      <Row>
        <Col>
          <FormGroup row>
            <TooltipLabel
              text="Login Key"
              colWidth={4}
              helpKey="number"
              section="header"
              resource="us"
              className="required"
            />
            <Col sm={8}>
              <Input
                id="us-number"
                name="number"
                value={number}
                {...inputProps}
                readOnly={!newModel}
              />
            </Col>
          </FormGroup>
          {!newModel && (
            <>
              <FormGroup row>
                <Label sm={4} for="us-firstName">
                  First Name
                </Label>
                <Col sm={8}>
                  <Input
                    id="us-firstName"
                    name="firstName"
                    value={firstName || ''}
                    readOnly
                  />
                </Col>
              </FormGroup>
              <FormGroup row>
                <Label sm={4} for="us-lastName">
                  Last Name
                </Label>
                <Col sm={8}>
                  <Input
                    id="us-lastName"
                    name="lastName"
                    value={lastName || ''}
                    readOnly
                  />
                </Col>
              </FormGroup>
              <FormGroup row>
                <Label sm={4} for="us-email">
                  Email
                </Label>
                <Col sm={8}>
                  <Input
                    id="us-email"
                    name="email"
                    value={email || ''}
                    readOnly
                  />
                </Col>
              </FormGroup>
              <FormGroup row>
                <Label sm={4} for="us-mobile">
                  Mobile
                </Label>
                <Col sm={8}>
                  <Input
                    id="us-mobile"
                    name="mobile"
                    value={mobile || ''}
                    readOnly
                  />
                </Col>
              </FormGroup>
            </>
          )}
        </Col>
        <Col>
          <FormGroup row>
            <TooltipLabel
              text="Role"
              colWidth={4}
              helpKey="roles"
              section="header"
              resource="us"
              className="required"
            />
            <Col sm={8}>
              <MultiSelect
                options={roleOptions}
                id="us-roles"
                name="roles"
                value={roles}
                {...inputProps}
                readOnly={!newModel || roleOptions.length === 0}
              />
            </Col>
          </FormGroup>
          <UserSalesUnit user={model} {...inputProps} newModel={newModel} />
          {!newModel && (
            <>
              <FormGroup row>
                <Label sm={4} for="us-uiVariant">
                  UI Variant
                </Label>
                <Col sm={8}>
                  <SingleSelect
                    options={uiVariantOptions}
                    id="us-uiVariant"
                    name="uiVariant"
                    value={uiVariant}
                    readOnly
                  />
                </Col>
              </FormGroup>
              <FormGroup row>
                <Label sm={4} for="us-team">
                  Team
                </Label>
                <Col sm={8}>
                  <Input id="us-team" name="team" value={team || ''} readOnly />
                </Col>
              </FormGroup>
              <FormGroup row>
                <Label sm={4} for="us-sapId">
                  SAP ID
                </Label>
                <Col sm={8}>
                  <Input
                    id="us-sapId"
                    name="sapId"
                    value={sapId || ''}
                    readOnly
                  />
                </Col>
              </FormGroup>
              <Row className="small">
                <Col sm="4" />
                <Col sm="8">
                  <TimestampInfo
                    timestamp={created}
                    actionName="created"
                    author={createdBy}
                    displayAttribute="inline"
                  />
                  <TimestampInfo
                    timestamp={lastModified}
                    actionName="lastModified"
                    author={lastModifiedBy}
                    displayAttribute="inline"
                  />
                </Col>
              </Row>
            </>
          )}
        </Col>
      </Row>
    </DiscardableForm>
  );
}

UserForm.propTypes = {
  model: oneOfType([newUserShape, userShape]).isRequired,
  onChange: func,
  onSubmit: func,
  onDiscard: func,
  newModel: bool,
};

UserForm.defaultProps = {
  onChange: undefined,
  onSubmit: undefined,
  onDiscard: undefined,
  newModel: false,
};

export default UserForm;
